// Bridge to the AEL Online Forms Firebase project (aeln8n-b70ab).
// The customer-facing forms stay hosted at forms.armellini.com — this module
// lets the CRM send invites and review submissions against that same backend.
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  signInWithCredential,
  signInWithPopup,
  onAuthStateChanged,
  GoogleAuthProvider,
  type AuthCredential,
  type User,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

// Public web config of the forms project (same values shipped in the forms app)
const FORMS_FIREBASE_CONFIG = {
  apiKey: "AIzaSyCXDleNLkGxlRLDcWxZrFTizy7dz1_dXko",
  authDomain: "aeln8n-b70ab.firebaseapp.com",
  projectId: "aeln8n-b70ab",
  storageBucket: "aeln8n-b70ab.firebasestorage.app",
  messagingSenderId: "697832650637",
  appId: "1:697832650637:web:85297b1d8d36a5745d2fc4",
};

export const FORMS_BASE_URL = "https://forms.armellini.com";

const FORMS_APP_NAME = "ael-forms";
const formsApp = getApps().some((a) => a.name === FORMS_APP_NAME)
  ? getApp(FORMS_APP_NAME)
  : initializeApp(FORMS_FIREBASE_CONFIG, FORMS_APP_NAME);

export const formsAuth = getAuth(formsApp);
export const formsDb = getFirestore(formsApp);

// ---------------------------------------------------------------------------
// Auth bridging — reuse the CRM's Google sign-in on the forms project
// ---------------------------------------------------------------------------

/** Sign into the forms project with the Google credential from the CRM's popup. */
export async function bridgeFormsAuth(credential: AuthCredential): Promise<void> {
  await signInWithCredential(formsAuth, credential);
}

/**
 * Silent bridge for restored sessions: reuse the CRM's stored Google access
 * token to sign into the forms project without any user interaction.
 * Returns false if the token is missing/expired (the caller can fall back).
 */
export async function bridgeFormsAuthWithToken(accessToken: string): Promise<boolean> {
  if (formsAuth.currentUser) return true;
  try {
    await signInWithCredential(formsAuth, GoogleAuthProvider.credential(null, accessToken));
    return true;
  } catch {
    return false;
  }
}

/** Last-resort fallback: explicit Google popup scoped to the forms project. */
export async function connectFormsWithPopup(): Promise<void> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ hd: "armellini.com", prompt: "select_account" });
  await signInWithPopup(formsAuth, provider);
}

export function onFormsAuthChanged(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(formsAuth, cb);
}

// ---------------------------------------------------------------------------
// Types (mirrors ael-online-forms/src/types/firestore.ts)
// ---------------------------------------------------------------------------

export interface RateAgreementLetterFields {
  letterDate: string;
  billingEmail: string;
  accountNumber: string;
  address: string;
  cityStateZip: string;
  originCityState: string;
  destinationCityState: string;
  firstShipmentDate: string;
  palletRate: string;
  cubeRateDetails: string;
  minimumShipmentDetails: string;
  paymentTerms: string;
  paymentTimeframe: string;
  lateFeePercentage: string;
  senderEmail: string;
  senderName: string;
}

export interface FormInvite {
  id: string;
  companyName: string;
  signerName: string;
  customerEmail: string;
  formType: string;
  createdBy?: string;
  createdAt?: Timestamp;
  status: "sent" | "opened" | "completed";
  rateAgreementFields?: RateAgreementLetterFields;
}

export interface FormApplication {
  id: string;
  inviteId: string;
  customerName: string;
  customerEmail: string;
  formData: any;
  einDriveUrl?: string | null;
  einFileName?: string | null;
  w9DriveUrl?: string | null;
  w9FileName?: string | null;
  signedAgreementDriveUrl?: string | null;
  status: "pending" | "completed";
  submittedAt?: Timestamp;
}

export const FORM_TYPES: { id: string; label: string }[] = [
  { id: "credit_application", label: "Credit Application" },
  { id: "w9", label: "W-9 Tax Form" },
  { id: "credit_card_authorization", label: "Credit Card Authorization" },
  { id: "rate_agreement_letter", label: "Rate Agreement Letter" },
];

export function formatFormType(type: string): string {
  return FORM_TYPES.find((t) => t.id === type)?.label
    || type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Matches the defaults shipped in the forms app's Management Console
export const DEFAULT_INSTRUCTIONS: Record<string, string> = {
  credit_application:
    "Please complete the credit application and upload your EIN Resale Certificate. Should you have any questions, please do not hesitate to contact me.\n\nKind Regards,\n\nArmellini Customer Center\nArmellini Logistics\nccenter@armellini.com",
  w9:
    "Please complete the W-9 Tax Form attached. Should you have any questions, please do not hesitate to contact me.\n\nKind Regards,\n\nArmellini Customer Center\nArmellini Logistics\nccenter@armellini.com",
  credit_card_authorization:
    "Please complete the Credit Card Authorization Form. Should you have any questions, please do not hesitate to contact me.\n\nKind Regards,\n\nArmellini Customer Center\nArmellini Logistics\nccenter@armellini.com",
  rate_agreement_letter:
    "Please review and sign the enclosed Rate Agreement Letter at your earliest convenience. Should you have any questions, please do not hesitate to contact me.\n\nKind Regards,\n\nArmellini Customer Center\nArmellini Logistics\nccenter@armellini.com",
};

// ---------------------------------------------------------------------------
// Firestore operations
// ---------------------------------------------------------------------------

export function subscribeInvites(cb: (invites: FormInvite[]) => void): () => void {
  const q = query(collection(formsDb, "invites"), orderBy("createdAt", "desc"), limit(100));
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as FormInvite));
  });
}

export function subscribeApplications(cb: (apps: FormApplication[]) => void): () => void {
  const q = query(collection(formsDb, "applications"), orderBy("submittedAt", "desc"), limit(100));
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as FormApplication));
  });
}

export function subscribeFormTemplates(
  cb: (templates: Record<string, { instructions: string }>) => void
): () => void {
  return onSnapshot(collection(formsDb, "formTemplates"), (snap) => {
    const data: Record<string, { instructions: string }> = {};
    snap.forEach((d) => { data[d.id] = d.data() as { instructions: string }; });
    cb(data);
  });
}

export async function saveFormTemplate(formType: string, instructions: string): Promise<void> {
  await setDoc(doc(formsDb, "formTemplates", formType), {
    instructions,
    updatedAt: serverTimestamp(),
    updatedBy: formsAuth.currentUser?.email || "unknown",
  });
}

export async function resetFormTemplate(formType: string): Promise<void> {
  await deleteDoc(doc(formsDb, "formTemplates", formType));
}

export async function createFormInvite(input: {
  companyName: string;
  signerName: string;
  customerEmail: string;
  formType: string;
  rateAgreementFields?: RateAgreementLetterFields;
}): Promise<{ inviteId: string; inviteLink: string }> {
  const ref = await addDoc(collection(formsDb, "invites"), {
    companyName: input.companyName,
    signerName: input.signerName,
    customerEmail: input.customerEmail,
    formType: input.formType,
    createdBy: formsAuth.currentUser?.uid,
    createdAt: serverTimestamp(),
    status: "sent",
    ...(input.formType === "rate_agreement_letter" && input.rateAgreementFields
      ? { rateAgreementFields: input.rateAgreementFields }
      : {}),
  });
  return { inviteId: ref.id, inviteLink: `${FORMS_BASE_URL}/apply/${ref.id}` };
}

export async function deleteFormInvite(id: string): Promise<void> {
  await deleteDoc(doc(formsDb, "invites", id));
}

/** Email the invite link via the forms project's SendGrid cloud function. */
export async function sendInviteEmail(input: {
  to: string;
  companyName: string;
  signerName: string;
  inviteLink: string;
  instructions?: string;
  formTitle?: string;
}): Promise<void> {
  const user = formsAuth.currentUser;
  if (!user) throw new Error("Not connected to the forms workspace");
  const idToken = await user.getIdToken();
  const res = await fetch(`${FORMS_BASE_URL}/api/send-invite`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    let detail = "Email service error";
    try {
      const data = await res.json();
      detail = data.error || detail;
    } catch { /* non-JSON error body */ }
    throw new Error(detail);
  }
}
